import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { QUESTION_DISPLAY_MODE } from '@hubertai/screening-question-types';
import type { TQuestionDisplayMode } from '@hubertai/screening-question-types';
import { useQuestionEditorContext } from '../context/create-question.context';
import DisplayModePreview from './DisplayModePreview';
import HubertHoverCard from 'components/HubertUI/HubertHoverCard';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSegmentedControl from 'components/HubertUI/HubertSegmentedControl';
import MutedText from 'components/common/MutedText';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionDisplayMode, selectQuestionMode } from 'rtk/questionEditorSelector';
import { updateDisplayMode } from 'rtk/Question.Editor.Slice';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';

// Hovering (or focusing) a segment shows what the candidate would see in that mode.
// The hover-card trigger writes its own data-state (open/closed) onto the same button, clobbering
// the "on" the segmented control's selected-indicator CSS keys off — so state it explicitly.
const ModeSegment = ({ mode, label, selected }: { mode: TQuestionDisplayMode; label: string; selected: boolean }) => (
  <HubertHoverCard openDelay={150} closeDelay={50}>
    <HubertHoverCard.Trigger asChild>
      <HubertSegmentedControl.Item value={mode} data-state={selected ? 'on' : 'off'}>
        {label}
      </HubertSegmentedControl.Item>
    </HubertHoverCard.Trigger>
    <HubertHoverCard.Content portalContent side="bottom" align="start" className="p-3">
      <DisplayModePreview mode={mode} />
    </HubertHoverCard.Content>
  </HubertHoverCard>
);

const SingleSelectDisplayModeSelect = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const displayMode = useAppSelector((state) => selectQuestionDisplayMode(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);

  const disabled = inTemplateEditAndUneditable;

  return (
    <div className={clsx(inTemplateEditAndUneditable && 'hidden', 'flex flex-col justify-start gap-4', 'p-1')}>
      <div
        className={clsx(
          inTemplateEditAndUneditable && 'hidden',
          'flex flex-row justify-start gap-4',
          'hubert-vertical-center',
        )}
      >
        <HubertLabel className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
          {t('displayMode.label')}
        </HubertLabel>
        <HubertSegmentedControl
          value={displayMode}
          disabled={disabled}
          onValueChange={(value: string) => {
            if (value === QUESTION_DISPLAY_MODE.Buttons || value === QUESTION_DISPLAY_MODE.SearchableList) {
              dispatch(updateDisplayMode({ questionId, value: value as TQuestionDisplayMode }));
            }
          }}
        >
          <ModeSegment
            mode={QUESTION_DISPLAY_MODE.Buttons}
            label={t('displayMode.buttons')}
            selected={displayMode === QUESTION_DISPLAY_MODE.Buttons}
          />
          <ModeSegment
            mode={QUESTION_DISPLAY_MODE.SearchableList}
            label={t('displayMode.searchableList')}
            selected={displayMode === QUESTION_DISPLAY_MODE.SearchableList}
          />
        </HubertSegmentedControl>
      </div>
      <div>
        <MutedText text={t('displayMode.hint')} />
      </div>
    </div>
  );
};

export default SingleSelectDisplayModeSelect;
